import {ConnectedProps, InferableComponentEnhancerWithProps} from 'react-redux'

export type Action<Type, Payload> = {type: Type; payload: Payload}

export type MapResultToPromise<T> = T extends (...args: infer U) => infer R
  ? (...args: U) => Promise<R>
  : T

export type ResolveLoops<DispatchProps> = {
  [Property in keyof DispatchProps]: MapResultToPromise<DispatchProps[Property]>
}

// This type allows to correctly map bound actions with redux loop
export type ConnectedLoopProps<DispatchProps, Connector> = Omit<
  ConnectedProps<Connector>,
  keyof DispatchProps
> & {
  [Property in keyof DispatchProps]: MapResultToPromise<DispatchProps[Property]>
}

export type Connector<
  TStateProps = unknown,
  TDispatchProps = unknown,
  TOwnProps = unknown,
> = InferableComponentEnhancerWithProps<TStateProps & ResolveLoops<TDispatchProps>, TOwnProps>

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

export type Overwrite<T, U> = Pick<T, Exclude<keyof T, keyof U>> & U

export type AttachmentId = string
export type VendorId = string

export enum ToastType {
  DEFAULT = 'default',
  WARNING = 'warning',
  ERROR = 'error',
  PROGRESS = 'progress',
}

export type DateRange = {
  from: string // ISO date string
  to: string // ISO date string
}

export type DateRangeConfig = {
  earliestFromTodayDays: number | null
  maxDaysRange: number
  defaultFromDays: number
  defaultToDays: number
  defaultMinDate?: string // ISO date string
}

export type FileId = string
export type OrderId = string

// MESSAGES
export type AttachmentFile = {
  file: File
  id: FileId
  name: string
  size: number
  type?: string
  src?: string
}

export enum MessageAccountType {
  CUSTOMER = 'customer',
  PARTNER = 'partner',
}

export type Attachment = {
  id: AttachmentId
  name: string
  url: string
}

export type Message = {
  text: string
  created: string
  author: {
    accountType: MessageAccountType
  }
  attachments?: Attachment[]
  unread: boolean
}

// REVIEWS
export type ProviderRating = {
  quality: number
  leadTime: number
  valueForMoney: number
  communication: number
  total: number
}

export type CraftcloudReview = {
  craftcloudExperience: number
  craftcloudComments: string
}

export type ProviderReview = ProviderRating &
  CraftcloudReview & {
    id: VendorId
    orderId: OrderId
    createdAt: string
    updatedAt: string
    vendorId: VendorId
    comments: string
    attachments: Attachment[]
    vendorReply: string | null
  }

export type ProviderRatingAggregated = ProviderRating & {
  count: number
}

export type ProviderSpecializationOption =
  | 'rapid_prototyping'
  | 'small_batch_production'
  | 'large_batch_production'
  | 'end_use_parts'
  | 'miniatures_and_figurines'
  | 'large_size_manufacturing'

export type ProviderServiceOfferOption =
  | '3d_printing'
  | 'cnc_machining'
  | 'vacuum_casting'
  | 'sheet_metal_fabrication'
  | 'injection_molding'
  | 'tapping_and_threading'
  | 'welding'
  | '3d_design_services'

export type ProviderCertificationOption =
  | 'iso_9001'
  | 'as_9100'
  | 'iso_13485'
  | 'iso_14001'
  | 'iso_ts16949'
